import Scene = Phaser.Scene;
import {Consumable, ConsumableType} from "@/store/consumable/types";
import {Keys} from "@/constants/key";
import TextUtils from "@/utils/text-utils";
import {EffectType} from "@/store/artifact/types";
import Image = Phaser.GameObjects.Image;
import Container = Phaser.GameObjects.Container;
import {Fonts} from "@/constants/font";
import CreaturePanel from "@/scenes.ui/creature-panel";
import AtlasUtils from "@/utils/atlas-utils";
import SpriteUtils from "@/utils/sprite-utils";
import CU from "@/utils/c-u";

export default class ConsumablePanel extends Phaser.GameObjects.Container {

    private onConsumableUseCallback!: (consumable: Consumable, container: Container, effectSound: string) => unknown;
    private onConsumableDragStartCallback!: (consumable: Consumable) => unknown;
    private onConsumableDragEndCallback!: () => unknown;

    private readonly OFFSET_Y: number = 33;
    private readonly OFFSET_X: number = 78;
    private readonly DEPTH: number = 1;
    private readonly BACKGROUND_PANEL_OFFSET_Y: number = 30;
    private readonly EFFECT_TYPE_ICON_OFFSET_X: number = 25;
    private readonly EFFECT_TYPE_ICON_OFFSET_Y: number = 15;

    private firstItem!: Container;
    private secondItem!: Container;
    private thirdItem!: Container;

    constructor(scene: Scene, canvas: HTMLCanvasElement, consumables: Consumable[],
                onConsumableUseCallback: (consumable: Consumable, container: Container, effectSound: string) => unknown,
                onConsumableDragStartCallback: (consumable: Consumable) => unknown,
                onConsumableDragEndCallback: () => unknown) {
        super(scene);

        this.scene = scene;
        let self = this;

        this.onConsumableUseCallback = onConsumableUseCallback;
        this.onConsumableDragStartCallback = onConsumableDragStartCallback;
        this.onConsumableDragEndCallback = onConsumableDragEndCallback;

        this.setDepth(this.DEPTH);

        let x = canvas!.width / CU.S;
        let y = canvas!.height / CU.S;

        let panel_background = SpriteUtils.addImage(this.scene, x / 2, y - this.BACKGROUND_PANEL_OFFSET_Y, Keys.ATLAS_UI, Keys.UI_CONS_PANEL_BACKGROUND);

        let slot1 = SpriteUtils.addImage(this.scene, x / 2 - this.OFFSET_X, y - this.OFFSET_Y, Keys.ATLAS_UI, Keys.UI_CONS_SLOT_BACKGROUND);
        let slot2 = SpriteUtils.addImage(this.scene, x / 2, y - this.OFFSET_Y, Keys.ATLAS_UI, Keys.UI_CONS_SLOT_BACKGROUND);
        let slot3 = SpriteUtils.addImage(this.scene, x / 2 + this.OFFSET_X, y - this.OFFSET_Y, Keys.ATLAS_UI, Keys.UI_CONS_SLOT_BACKGROUND);

        let slot1_border = SpriteUtils.addImage(this.scene, slot1.x / CU.S, slot1.y / CU.S, Keys.ATLAS_UI, Keys.UI_CONS_SLOT_BORDER);
        let slot2_border = SpriteUtils.addImage(this.scene, slot2.x / CU.S, slot2.y / CU.S, Keys.ATLAS_UI, Keys.UI_CONS_SLOT_BORDER);
        let slot3_border = SpriteUtils.addImage(this.scene, slot3.x / CU.S, slot3.y / CU.S, Keys.ATLAS_UI, Keys.UI_CONS_SLOT_BORDER);

        this.add([panel_background, slot1, slot2, slot3, slot1_border, slot2_border, slot3_border]);

        if (consumables[0]) {
            this.firstItem = this.setItemInteractionsAndEffectInfo(slot1, consumables[0]);
        }

        if (consumables[1]) {
            this.secondItem = this.setItemInteractionsAndEffectInfo(slot2, consumables[1]);
        }
        if (consumables[2]) {
            this.thirdItem = this.setItemInteractionsAndEffectInfo(slot3, consumables[2]);
        }

        this.enableConsumableInteractions();
    }

    public enableConsumableInteractions(){
        if (this.firstItem) { this.setItemInteractive(this.firstItem); }
        if (this.secondItem) { this.setItemInteractive(this.secondItem); }
        if (this.thirdItem) { this.setItemInteractive(this.thirdItem); }
    }

    public disableConsumableInteractions(){
        if (this.firstItem) { this.setItemNotInteractive(this.firstItem); }
        if (this.secondItem) { this.setItemNotInteractive(this.secondItem); }
        if (this.thirdItem) { this.setItemNotInteractive(this.thirdItem); }
    }

    private setItemInteractionsAndEffectInfo(slot: Image, consumable: Consumable): Container {

        let container = new Container(this.scene);

        let atlas = AtlasUtils.getConsumableNoShadeAtlas(consumable.imageId) || consumable.imageId;
        let image = atlas != consumable.imageId? consumable.imageId : undefined;

        let item = SpriteUtils.addImage(this.scene, slot.x / CU.S, slot.y / CU.S, atlas, image);

        let effectTypeIcon = SpriteUtils.addImage(this.scene, slot.x / CU.S - this.EFFECT_TYPE_ICON_OFFSET_X, slot.y / CU.S + this.EFFECT_TYPE_ICON_OFFSET_Y,
            Keys.ATLAS_UI, this.determineEffectTypeIcon(consumable.effectType));

        let effectTypeValueText = TextUtils.addTextByFamily(this.scene, this.determineEffectTypeText(consumable),
            effectTypeIcon.x / CU.S, effectTypeIcon.y / CU.S, "#ffffff", 9, "normal", 50, Fonts.MYRIADPRO_BOLD);
        effectTypeValueText.setStroke('#000000', 1);

        container.add([item, effectTypeIcon, effectTypeValueText]);

        this.setItemInteractions(container, consumable, slot.x, slot.y);

        this.add(container);

        return container;

    }

    private setItemInteractive(container: Container) {
        let item = this.getItemImageFromContainer(container);
        if (item && item!.active) { item!.setInteractive({draggable: true}) }
    }

    private setItemNotInteractive(container: Container) {
        let item = this.getItemImageFromContainer(container);
        if (item && item!.active) { item!.disableInteractive() }
    }

    private setItemInteractions(container: Container, consumable: Consumable, x: number, y: number) {

        let self = this;

        let item = this.getItemImageFromContainer(container);

        item.on('dragstart', function(pointer: any, dragX: any, dragY: any){
                if (self.onConsumableDragStartCallback != null) {
                    self.onConsumableDragStartCallback(consumable);
                }
            })
            .on('drag', function(pointer: any, dragX: any, dragY: any){
                item.setPosition(dragX, dragY);

            })
            .on('dragend', function(pointer: any, dragX: any, dragY: any, dropped: any){
                item.setPosition(x, y);
                if (self.onConsumableUseCallback != null) {
                    self.onConsumableUseCallback(consumable, container, self.determineEffectSound(consumable));
                }
                if (self.onConsumableDragEndCallback != null) {
                    self.onConsumableDragEndCallback();
                }
            })
    }

    private getItemImageFromContainer(container: Container): Image {
        return container!.first as Image;
    }

    private determineEffectTypeIcon(effectType: EffectType | undefined) {
        switch (effectType) {
            case EffectType.ATTACK:
                return Keys.UI_CONS_EFFECT_ATTACK;
            case EffectType.DEFENCE:
                return Keys.UI_CONS_EFFECT_DEFENCE;
            case EffectType.HEALTH:
                return Keys.UI_CONS_EFFECT_HEALTH;
            case EffectType.KOSCHEY_DEFENCE:
                return Keys.UI_CONS_EFFECT_DEFENCE;
            case EffectType.POISON:
                return Keys.UI_CONS_EFFECT_DEFENCE;
            case EffectType.RESURRECTION:
                return Keys.UI_CONS_EFFECT_DEFENCE;
        }
        throw new TypeError();
    }

    private determineEffectTypeText(consumable: Consumable | undefined) {
        switch (consumable!.consumableType) {
            case ConsumableType.PLAYER:
                return "+" + TextUtils.formatMoney(consumable!.effectValue);
            case ConsumableType.ENEMY:
                return "-" + TextUtils.formatMoney(consumable!.effectValue);
            case ConsumableType.ANY:
                return TextUtils.formatMoney(consumable!.effectValue);
        }
        throw new TypeError();
    }

    private determineEffectSound(consumable: Consumable | undefined) {
        switch (consumable!.consumableType) {
            case ConsumableType.PLAYER:
                return Keys.SOUND_USE_HEAL_POTION;
            case ConsumableType.ENEMY:
                return Keys.SOUND_USE_SCROLL;
            case ConsumableType.ANY:
                return Keys.SOUND_USE_SCROLL;
        }
        throw new TypeError();
    }

    private determinePosOffsetByEffect(effectType: EffectType | undefined): number {
        switch (effectType) {
            case EffectType.ATTACK:
                return -25;
            case EffectType.DEFENCE:
                return 25;
            case EffectType.HEALTH:
                return 0;
        }
        throw new TypeError();
    }
}

